import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Code2, Server, Network, ExternalLink, Github,
  Mail, MapPin, Phone, Send, User, Linkedin, MessageCircle,
  Briefcase, Building2, Calendar, Award, Quote, Download,
  Terminal, GitBranch, Globe, Shield,
} from 'lucide-react';

import { portfolioData } from '../mock';
import { portfolioDataId } from '../data/portfolioDataId';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProjectWebPreview from '../components/ProjectWebPreview';
import { useLanguage } from '../contexts/LanguageContext';

/* ─── helpers ─────────────────────────────────────────────── */
const featureIcons = [Server, Network, Code2];
const ctaServiceIcons = [Server, Terminal, GitBranch, Code2, Globe, Shield];

const SectionLabel = ({ number, label }) => (
  <div className="flex items-center gap-3 mb-4 justify-center">
    <span className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
      {number} · {label}
    </span>
  </div>
);

/* ─── main component ───────────────────────────────────────── */
const Home = () => {
  const { language, t } = useLanguage();
  const pd = language === 'id' ? portfolioDataId : portfolioData;

  const screenshotMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('screenshot') === '1';
  }, []);

  useEffect(() => {
    if (window.location.hash === '#projects') {
      requestAnimationFrame(() => {
        document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, []);

  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState('idle'); // idle | sending | sent | error
  const [selectedCategory, setSelectedCategory] = useState('all');

  const gmailUrl = (subject, body) => {
    const to = encodeURIComponent(portfolioData.email);
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'e25aaed5-e848-4cef-86eb-bf1abd9ff8a7',
          subject: `Pesan baru dari ariftech.my.id - ${contactForm.name}`,
          from_name: 'Website ariftech.my.id',
          name: contactForm.name,
          email: contactForm.email,
          message: contactForm.message,
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message || 'Form submit failed');
      setContactStatus('sent');
      setContactForm({ name: '', email: '', message: '' });
      toast({ title: language === 'id' ? 'Pesan terkirim!' : 'Message sent!', description: language === 'id' ? 'Saya akan segera menghubungi Anda.' : "I'll get back to you soon." });
    } catch {
      setContactStatus('error');
      toast({ title: 'Error', description: language === 'id' ? 'Gagal mengirim. Silakan coba lagi.' : 'Failed to send. Please try again.' });
    }
  };

  const handleInputChange = (e) =>
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });

  /* helper: merge English base with translated description */
  const allProjects = portfolioData.projects.map((p) => {
    const idData = portfolioDataId.projects.find((q) => q.id === p.id);
    return language === 'id' && idData ? { ...p, description: idData.description } : p;
  });

  const projectCategories = ['all', ...Array.from(new Set(portfolioData.projects.map((p) => p.category)))];

  const mergedProjects = selectedCategory === 'all'
    ? allProjects
    : allProjects.filter((p) => p.category === selectedCategory);

  const mergedExperience = portfolioData.experience.map((e) => {
    const idData = portfolioDataId.experience.find((q) => q.id === e.id);
    return language === 'id' && idData ? { ...e, description: idData.description } : e;
  });

  const mergedEducation = portfolioData.education.map((e) => {
    const idData = portfolioDataId.education.find((q) => q.id === e.id);
    return language === 'id' && idData
      ? { ...e, degree: idData.degree, detail: idData.detail }
      : e;
  });

  const mergedTestimonials = portfolioData.testimonials.map((t) => {
    const idData = portfolioDataId.testimonials.find((q) => q.id === t.id);
    return language === 'id' && idData ? { ...t, content: idData.content } : t;
  });

  const currentSkills = language === 'id' ? portfolioDataId.skills : portfolioData.skills;

  return (
    <div className="min-h-screen bg-white font-sans">
      {!screenshotMode && <Navbar />}

      {/* ════════════════════════════════════════
          HERO
      ════════════════════════════════════════ */}
      <section
        id="home"
        className={`min-h-screen flex items-center relative overflow-hidden bg-white ${
          screenshotMode ? 'pt-8' : 'pt-16'
        }`}
      >
        {/* Background: subtle grid + blobs */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, #d1fae5 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            opacity: 0.45,
          }}
        />
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-emerald-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-teal-300/20 rounded-full blur-3xl" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Left — text content */}
            <div className="space-y-8 text-center lg:text-left order-2 lg:order-1">
              <div className="space-y-4">
                <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-1.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {t.hero.badge}
                </span>
                <div>
                  <p className="text-emerald-600 font-medium text-lg mb-1">{t.hero.greeting}</p>
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight tracking-tight">
                    Arif{' '}
                    <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                      Wahyudi
                    </span>
                  </h1>
                </div>
                <p className="text-xl text-gray-500 font-light">{pd.title}</p>
              </div>

              <p className="text-gray-600 text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
                {pd.tagline}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Button
                  onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-base font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 group rounded-xl"
                >
                  {t.hero.viewWork}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
                <a
                  href="/cv.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 px-8 py-3.5 text-base font-semibold transition-all duration-300 rounded-xl"
                >
                  <Download size={18} />
                  {language === 'id' ? 'Download CV' : 'Download CV'}
                </a>
              </div>

              {/* Quick facts row */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center lg:justify-start gap-x-3 gap-y-3 sm:gap-4 pt-2">
                {(pd.stats || portfolioData.stats).map((s, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-center sm:text-left">
                    <span className="text-xl font-bold text-gray-900 leading-tight">{s.value}</span>
                    <span className="text-xs sm:text-sm text-gray-400 leading-tight">{s.label}</span>
                    {i < (pd.stats || portfolioData.stats).length - 1 && (
                      <span className="hidden sm:inline text-gray-200 sm:ml-2">|</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Profile Photo */}
            <div className="flex justify-center items-center order-1 lg:order-2 py-16">
              <div className="relative w-72 h-72 sm:w-80 sm:h-80">

                {/* Rotating dashed outer ring */}
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-emerald-200/80 animate-[spin_40s_linear_infinite]" />
                {/* Static inner rings */}
                <div className="absolute inset-5 rounded-full border border-emerald-100/80" />

                {/* Profile photo */}
                <div className="absolute inset-8 rounded-full overflow-hidden border-4 border-white shadow-2xl ring-4 ring-emerald-100">
                  <img
                    src="/profile.jpg"
                    alt="Arif Wahyudi"
                    className="w-full h-full object-cover object-center"
                  />
                </div>

                {/* Floating badges — anchored to ring edges */}
                {/* Top */}
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-white shadow-lg border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  <Server size={13} className="text-emerald-500" /> IT Manager
                </div>
                {/* Right */}
                <div className="absolute top-1/2 -translate-y-1/2 -right-20 bg-white shadow-lg border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  <Code2 size={13} className="text-teal-500" /> Full Stack Dev
                </div>
                {/* Bottom-right */}
                <div className="absolute -bottom-5 right-4 bg-white shadow-lg border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  <Network size={13} className="text-emerald-500" /> Infra
                </div>
                {/* Left */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-16 bg-white shadow-lg border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  🐧 Linux
                </div>
                {/* Bottom-left */}
                <div className="absolute -bottom-5 left-4 bg-white shadow-lg border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  🐳 Docker
                </div>
              </div>
            </div>
          </div>

          {/* Feature pills */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-24">
            {t.hero.features.map((item, idx) => {
              const Icon = featureIcons[idx];
              return (
                <div
                  key={idx}
                  className="group flex items-start gap-4 bg-white border border-gray-100 rounded-2xl px-6 py-5 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                    <Icon className="text-emerald-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          HERO CTA — What I Can Build For You
      ════════════════════════════════════════ */}
      <section className="bg-gray-900 py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #10b981 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.07 }}
        />
        <div className="absolute -top-40 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 right-1/3 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-medium px-4 py-1.5 rounded-full mb-5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {t.heroCta.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.heroCta.title}{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                {t.heroCta.highlight}
              </span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">{t.heroCta.subtitle}</p>
          </div>

          {/* Service grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {ctaServiceIcons.map((Icon, i) => {
              const svc = t.heroCta.services[i];
              if (!svc) return null;
              return (
                <div
                  key={i}
                  className="group bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all duration-300 text-center"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 flex items-center justify-center mx-auto mb-3 transition-colors">
                    <Icon className="text-emerald-400" size={20} />
                  </div>
                  <h3 className="text-white text-xs font-semibold mb-1 leading-tight">{svc.title}</h3>
                  <p className="text-gray-500 text-[10px] leading-relaxed">{svc.desc}</p>
                </div>
              );
            })}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={portfolioData.services.fastwork.url}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-300 text-sm shadow-lg shadow-blue-500/25"
            >
              <ExternalLink size={16} /> {t.heroCta.hireOnFastwork}
            </a>
            <a
              href={portfolioData.services.fiverr.url}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-300 text-sm shadow-lg shadow-green-500/25"
            >
              <ExternalLink size={16} /> {t.heroCta.orderOnFiverr}
            </a>
            <a
              href={`https://wa.me/${portfolioData.whatsapp}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 hover:bg-white/10 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-300 text-sm"
            >
              <MessageCircle size={16} /> {t.heroCta.chatWhatsApp}
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          ABOUT
      ════════════════════════════════════════ */}
      <section id="about" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionLabel number="01" label={t.nav.about} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.about.title}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.about.highlight}
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{t.about.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            {/* Bio + certifications — 2 cols */}
            <div className="lg:col-span-2 space-y-8">
              <p className="text-gray-700 text-base leading-loose whitespace-pre-line">
                {pd.bio}
              </p>

              {/* Certifications */}
              {portfolioData.certifications && portfolioData.certifications.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Award size={15} className="text-teal-500" />
                    {t.about.certifications}
                  </h3>
                  <div className="space-y-3">
                    {portfolioData.certifications.map((cert) => (
                      <div
                        key={cert.id}
                        className="bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-teal-300 hover:shadow-sm transition-all"
                      >
                        <h4 className="text-gray-900 text-sm font-semibold">{cert.name}</h4>
                        <p className="text-teal-600 text-xs mt-0.5">{cert.issuer}</p>
                        <p className="text-gray-400 text-xs mt-0.5">
                          {t.about.issued} {cert.issued} · {t.about.expires} {cert.expires}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {cert.skills.map((s) => (
                            <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Experience timeline — 3 cols */}
            <div className="lg:col-span-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Briefcase size={15} className="text-emerald-500" />
                {t.about.workExp}
              </h3>

              <div className="relative pl-6">
                {/* vertical line */}
                <div className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-emerald-400 via-teal-300 to-transparent" />

                <div className="space-y-5">
                  {mergedExperience.map((exp, i) => (
                    <div key={exp.id} className="relative group">
                      {/* dot */}
                      <div className="absolute -left-4 top-3.5 w-4 h-4 rounded-full bg-white border-2 border-emerald-400 shadow-sm group-hover:bg-emerald-400 transition-colors" />

                      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300 ml-2">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                          <div>
                            <h4 className="text-gray-900 font-semibold text-sm">{exp.title}</h4>
                            <p className="text-emerald-600 text-xs font-medium flex items-center gap-1 mt-0.5">
                              <Building2 size={11} /> {exp.company}
                            </p>
                          </div>
                          <span className="flex items-center gap-1 text-gray-400 text-xs whitespace-nowrap">
                            <Calendar size={11} /> {exp.period}
                          </span>
                        </div>
                        <p className="text-gray-600 text-xs leading-relaxed">{exp.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          EDUCATION
      ════════════════════════════════════════ */}
      <section id="education" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionLabel number="02" label={t.nav.education} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.education.title}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.education.highlight}
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{t.education.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {mergedEducation.map((edu) => (
              <div
                key={edu.id}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 shadow-sm group"
              >
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-emerald-100 transition-colors">
                  <Award className="text-emerald-600" size={22} />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-1">{edu.degree}</h3>
                <p className="text-emerald-600 text-sm font-medium mb-1">{edu.school}</p>
                <p className="text-gray-400 text-xs mb-4 flex items-center gap-1">
                  <Calendar size={11} /> {edu.period}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">{edu.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          PROJECTS
      ════════════════════════════════════════ */}
      <section id="projects" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SectionLabel number="03" label={t.nav.projects} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.projects.title}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.projects.highlight}
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{t.projects.subtitle}</p>
          </div>

          {/* Demo notice banner */}
          <div className="mb-10 max-w-3xl mx-auto bg-white border border-emerald-200 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-start gap-4 shadow-sm">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mt-0.5">
              <Briefcase className="text-emerald-600" size={18} />
            </div>
            <div className="flex-1">
              <p className="text-gray-900 text-sm font-bold mb-1">{t.projects.bannerTitle}</p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t.projects.bannerDesc}{' '}
                <a href={gmailUrl('Custom Project Inquiry', '')} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                  {t.projects.bannerEmail}
                </a>{' '}
                {t.projects.bannerOr}{' '}
                <a href={`https://wa.me/${portfolioData.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                  {t.projects.bannerWhatsapp}
                </a>.
              </p>
            </div>
          </div>

          {/* Category filter */}
          <div className="mb-10 relative">
            {/* Left fade */}
            <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none rounded-l-full" />
            {/* Right fade */}
            <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none rounded-r-full" />

            <div
              className="flex gap-2 overflow-x-auto px-2 py-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {projectCategories.map((cat) => {
                const label = cat === 'all'
                  ? t.projects.categories.all
                  : (t.projects.categories[cat] || cat);
                const count = cat === 'all'
                  ? allProjects.length
                  : allProjects.filter((p) => p.category === cat).length;
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`flex-shrink-0 inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full border transition-all duration-200 ${
                      isActive
                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 hover:shadow-sm'
                    }`}
                  >
                    {label}
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                        isActive
                          ? 'bg-white/25 text-white'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {mergedProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-xl hover:border-emerald-200 hover:-translate-y-1 transition-all duration-300 group"
              >
                {/* Preview image */}
                <div className="relative overflow-hidden h-48 bg-gray-50">
                  <ProjectWebPreview
                    title={project.title}
                    previewLayout={project.previewLayout || 'hrms'}
                    image={project.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-6">
                  <div className="mb-3">
                    <span className="inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full mb-2">
                      {project.category}
                    </span>
                    <h3 className="text-gray-900 font-bold text-base leading-snug">{project.title}</h3>
                  </div>

                  <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4">
                    {project.description}
                  </p>

                  {project.features && (
                    <div className="mb-4">
                      <p className="text-gray-700 text-xs font-semibold mb-2">{t.projects.keyFeatures}</p>
                      <ul className="space-y-1">
                        {project.features.slice(0, 3).map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
                            <span className="text-emerald-400 mt-0.5">▸</span> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tech badges */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {project.technologies.map((tech) => (
                      <span key={tech} className="text-xs text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    {project.repoUrl ? (
                      <a href={project.repoUrl} target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-xl py-2.5 hover:bg-gray-50 hover:border-gray-300 transition-all">
                        <Github size={14} /> {t.projects.code}
                      </a>
                    ) : (
                      <span className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-300 border border-gray-100 rounded-xl py-2.5 cursor-not-allowed">
                        <Github size={14} /> {t.projects.code}
                      </span>
                    )}
                    {project.liveUrl ? (
                      /^https?:\/\//i.test(project.liveUrl) ? (
                        <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl py-2.5 transition-colors shadow-sm">
                          <ExternalLink size={14} /> {t.projects.demo}
                        </a>
                      ) : (
                        <Link to={project.liveUrl}
                          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl py-2.5 transition-colors shadow-sm">
                          <ExternalLink size={14} /> {t.projects.demo}
                        </Link>
                      )
                    ) : (
                      <button
                        className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl py-2.5 transition-colors"
                        onClick={() => toast({ title: t.projects.demoUnavailable, description: t.projects.demoUnavailableDesc })}
                      >
                        <ExternalLink size={14} /> {t.projects.demo}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-14">
            <p className="text-gray-500 text-base mb-6">{t.projects.ctaText}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={gmailUrl('Custom Project Inquiry', '')} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-md shadow-emerald-500/20 text-sm"
              >
                <Mail size={16} /> {t.projects.emailMe}
              </a>
              <a
                href={`https://wa.me/${portfolioData.whatsapp}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm"
              >
                <MessageCircle size={16} /> {t.projects.whatsapp}
              </a>
              <a
                href={portfolioData.social.linkedin} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-gray-200 hover:border-blue-300 text-gray-700 hover:text-blue-700 hover:bg-blue-50 font-semibold px-6 py-3 rounded-xl transition-all text-sm"
              >
                <Linkedin size={16} /> LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          FREELANCE SERVICES
      ════════════════════════════════════════ */}
      <section id="services" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionLabel number="04" label={t.services ? t.services.title + ' ' + t.services.highlight : 'Freelance Services'} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.services ? t.services.title : 'Freelance'}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.services ? t.services.highlight : 'Services'}
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              {t.services ? t.services.subtitle : 'Available on leading freelance platforms for IT infrastructure and web development.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Fastwork */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600" />
              <div className="p-7">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <span className="text-blue-600 font-black text-sm">FW</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Fastwork</p>
                      <p className="text-xs text-gray-400">fastwork.id</p>
                    </div>
                  </div>
                  <a
                    href={portfolioData.services.fastwork.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                  >
                    <ExternalLink size={11} />
                    {t.services ? t.services.viewProfile : 'View Profile'}
                  </a>
                </div>
                <ul className="space-y-2.5">
                  {portfolioData.services.fastwork.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-blue-400 mt-0.5 flex-shrink-0">▸</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <a
                    href={portfolioData.services.fastwork.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-3 rounded-xl transition-colors"
                  >
                    {t.services ? t.services.orderNow : 'Order Now'} <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Fiverr */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-500" />
              <div className="p-7">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                      <span className="text-green-600 font-black text-sm">5r</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Fiverr</p>
                      <p className="text-xs text-gray-400">fiverr.com</p>
                    </div>
                  </div>
                  <a
                    href={portfolioData.services.fiverr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-green-600 border border-green-200 hover:bg-green-50 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                  >
                    <ExternalLink size={11} />
                    {t.services ? t.services.viewProfile : 'View Profile'}
                  </a>
                </div>
                <ul className="space-y-2.5">
                  {portfolioData.services.fiverr.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-green-400 mt-0.5 flex-shrink-0">▸</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <a
                    href={portfolioData.services.fiverr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-3 rounded-xl transition-colors"
                  >
                    {t.services ? t.services.orderNow : 'Order Now'} <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          SKILLS
      ════════════════════════════════════════ */}
      <section id="skills" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionLabel number="05" label={t.nav.skills} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.skills.title}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.skills.highlight}
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{t.skills.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {currentSkills.map((group) => (
              <div key={group.category} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-gray-900 font-bold text-sm uppercase tracking-widest mb-5 pb-3 border-b border-gray-100">
                  {group.category}
                </h3>
                <div className="space-y-4">
                  {group.items.map((skill) => (
                    <div key={skill.name}>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-gray-700 text-sm">{skill.name}</span>
                        <span className="text-emerald-600 text-xs font-semibold tabular-nums">{skill.level}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full"
                          style={{ width: `${skill.level}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{t.skills.testimonialTitle}</h3>
              <p className="text-gray-500 max-w-xl mx-auto text-sm">{t.skills.testimonialSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {mergedTestimonials.map((testimonial) => {
                const avatarColors = [
                  'bg-emerald-100 text-emerald-700',
                  'bg-blue-100 text-blue-700',
                  'bg-violet-100 text-violet-700',
                  'bg-amber-100 text-amber-700',
                  'bg-rose-100 text-rose-700',
                  'bg-teal-100 text-teal-700',
                ];
                const color = avatarColors[(testimonial.id - 1) % avatarColors.length];
                const platformLabel = testimonial.platform === 'fastwork' ? 'Fastwork'
                  : testimonial.platform === 'fiverr' ? 'Fiverr' : 'Direct';
                const platformColor = testimonial.platform === 'fastwork'
                  ? 'bg-blue-50 text-blue-600 border-blue-100'
                  : testimonial.platform === 'fiverr'
                    ? 'bg-green-50 text-green-600 border-green-100'
                    : 'bg-gray-50 text-gray-500 border-gray-200';

                return (
                  <div
                    key={testimonial.id}
                    className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300 flex flex-col gap-4"
                  >
                    {/* Stars */}
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: testimonial.rating || 5 }).map((_, i) => (
                        <span key={i} className="text-amber-400 text-sm">★</span>
                      ))}
                    </div>

                    <p className="text-gray-700 text-sm leading-relaxed flex-1">
                      "{testimonial.content}"
                    </p>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${color}`}>
                          {testimonial.initials || <User size={13} />}
                        </div>
                        <div>
                          <p className="text-gray-900 text-sm font-semibold leading-tight">{testimonial.name || t.skills.privateClient}</p>
                          {testimonial.role && (
                            <p className="text-gray-400 text-xs leading-tight">{testimonial.role}{testimonial.company ? `, ${testimonial.company}` : ''}</p>
                          )}
                        </div>
                      </div>
                      {testimonial.platform && (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${platformColor}`}>
                          {platformLabel}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          CONTACT
      ════════════════════════════════════════ */}
      <section id="contact" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionLabel number="06" label={t.nav.contact} />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.contact.title}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t.contact.highlight}
              </span>
            </h2>
            <p className="text-gray-600 text-lg">{t.contact.subtitle}</p>
            <p className="text-gray-400 text-sm mt-2 max-w-lg mx-auto">{t.contact.description}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-5xl mx-auto">
            {/* Links side — 2 cols */}
            <div className="lg:col-span-2 space-y-3">
              <a href={`mailto:${portfolioData.email}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 text-emerald-700 px-5 py-4 rounded-xl transition-all">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.contact.sendEmail}</p>
                  <p className="text-xs text-gray-400">{portfolioData.email}</p>
                </div>
              </a>
              <a href={`https://wa.me/${portfolioData.whatsapp}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-50 border border-green-200 hover:border-green-400 hover:bg-green-100 text-green-700 px-5 py-4 rounded-xl transition-all">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.contact.whatsapp}</p>
                  <p className="text-xs text-gray-400">{portfolioData.phone}</p>
                </div>
              </a>
              <a href={portfolioData.social.linkedin} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 bg-blue-50 border border-blue-200 hover:border-blue-400 hover:bg-blue-100 text-blue-700 px-5 py-4 rounded-xl transition-all">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Linkedin size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.contact.linkedin}</p>
                  <p className="text-xs text-gray-400">linkedin.com/in/arif-wahyudi-3a7829224</p>
                </div>
              </a>
            </div>

            {/* Form — 3 cols */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h3 className="text-gray-900 font-bold text-xl mb-1">{t.contact.formTitle}</h3>
                <p className="text-gray-500 text-sm mb-6">{t.contact.formSubtitle}</p>

                <form
                  onSubmit={handleContactSubmit}
                  name="contact"
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t.contact.namePlaceholder}</label>
                      <input
                        type="text" name="name"
                        placeholder={t.contact.namePlaceholder}
                        value={contactForm.name}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t.contact.emailPlaceholder}</label>
                      <input
                        type="email" name="email"
                        placeholder={t.contact.emailPlaceholder}
                        value={contactForm.email}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t.contact.messagePlaceholder}</label>
                    <textarea
                      name="message"
                      placeholder={t.contact.messagePlaceholder}
                      value={contactForm.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={contactStatus === 'sending'}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-md shadow-emerald-500/20 hover:shadow-emerald-500/30 text-sm"
                  >
                    <Send size={15} />
                    {contactStatus === 'sending'
                      ? (language === 'id' ? 'Mengirim…' : 'Sending…')
                      : contactStatus === 'sent'
                        ? (language === 'id' ? 'Terkirim ✓' : 'Sent ✓')
                        : t.contact.sendButton}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!screenshotMode && <Footer />}

      {/* Floating WhatsApp button */}
      {!screenshotMode && (
        <a
          href={`https://wa.me/${portfolioData.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-xl shadow-green-500/40 hover:shadow-green-500/60 transition-all duration-300 hover:scale-105 pl-4 pr-4 py-3.5"
        >
          <MessageCircle size={22} />
          <span className="hidden sm:inline text-sm font-semibold whitespace-nowrap">
            {language === 'id' ? 'Chat WhatsApp' : 'Chat on WhatsApp'}
          </span>
        </a>
      )}
    </div>
  );
};

export default Home;
